import React, { useState, useEffect } from 'react';
import { useSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CancelIcon from '@mui/icons-material/Cancel';
import { Modal, Typography, Divider, Grid } from '@mui/material';
import { orderRetryShipment, orderUpdate } from "../services/ordersService";
import { currencyFormat, escapeComma, formatDate } from '../utils/utils';
import ChipLabel from './ChipLabel';
import { chipStatus, paymentMethodType } from '../utils/enums';
import ButtonStyled from './ButtonStyled';

import OrderBoxDetail from './OrderBoxDetail';
import IOSSwitch from './IOSSwitch';
import { userGetPresignedUrlForCfdiByCartId } from '../services/userService';

const styleModal = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: {xs: '92%', md: '84%', lg: '64%'},
    bgcolor: 'background.paper',
    borderRadius: '0.25rem',
    boxShadow: 24,
    py: '2rem',
    px: {xs:'1rem', md: '2rem'},
    flexGrow: 1,
    '&:focus-visible': { outline: 0}
};

const customStyle = {   
    preventOverflow: {
      whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    },
    twoLinesOverflow: {
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden'
    },
    iconPagination: {
      padding: 0,
      color:'#303030'
    },
    descriptionDiv: {
        display: { md:'flex'},
        gap: {md: '0.5rem'}
    },
    priceResumeDiv: {
        display: 'flex',
        gap: '0.75rem',
        justifyContent: 'flex-end'
    },
    subtitle: {
        color: '#303030',
        fontSize: {xs:'0.875rem', lg:'1rem'},
        fontWeight: 500,
        lineHeight: {xs:'1rem', lg:'1.125rem'}, 
        letterSpacing: '0.01563rem',
    },
    subtitleBold: {
        fontWeight: 'bold', 
        fontSize: {xs:'0.875rem', lg:'1rem'},
        lineHeight: {xs: '1.125rem'}, 
        color: '#303030'
    },
    description: {
        color: '#514F4F',
        fontSize: {xs:'0.875rem', lg:'1rem'},
        fontWeight: 400,
        lineHeight: {xs:'1.125rem', lg:'1.25rem'}, 
    }

};

const OrderDetailModal = ({ reloadTableData, order, openModal, onCloseModal}) => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState(false);
    const [currentOrder, setCurrentOrder] = useState(order);

    const [checked, setChecked] = useState(!!order?.isPackaged);
    const [boxes, setBoxes] = useState([]);
    useEffect(() => {
        setCurrentOrder(order);
        setChecked(!!order?.isPackaged);
        setBoxes(JSON.parse(JSON.stringify(order?.shipment || [])));
    }, [order]);

    const shipmentRetryError = currentOrder?.shipmentRetryError;
    const canRetryShipment =
        currentOrder?.status === 'created' &&
        (currentOrder?.shipment || []).some((box) => !box?.tracking_data?.tracking_number);

    const getCfdi = async () => {
        if (isLoading) return;

        setIsLoading(true);
        try {
            // Misma idea que client-portal: abrir la URL firmada de S3 directamente.
            // Evita fallos por CORS/revoke con fetch+blob; la firma ya autoriza el GET.
            const presignedUrl = await userGetPresignedUrlForCfdiByCartId(currentOrder._id);
            if (!presignedUrl) {
                enqueueSnackbar('No se pudo obtener el enlace del CFDI.', {
                    variant: 'error',
                });
                return;
            }
            const newWindow = window.open(presignedUrl, '_blank', 'noopener,noreferrer');
            if (!newWindow) {
                enqueueSnackbar(
                    'El navegador bloqueó la ventana emergente. Permite ventanas emergentes para ver el CFDI.',
                    { variant: 'warning' }
                );
            }
        } catch (error) {
            console.error('Error al obtener el CFDI:', error);
            enqueueSnackbar('Ocurrió un problema al obtener el CFDI.', {
                variant: 'error',
            });
        } finally {
            setIsLoading(false);
        }
    };


    function generateOrderCSV(orderData) {
        // Initialize rows array
        const rows = [];
      
        // Iterate through each shipment/box in the order
        orderData.shipment.forEach((box, boxIndex) => {
            box.products.forEach((productItem, productIndex) => {
                const product = productItem.product;
        
                rows.push(['# de Rastreo', box.tracking_data?.tracking_number || 'Sin guía'].join(','));
                // # de Orden = SKU, Quantity
                rows.push([productItem.sku, productItem.quantity].join(','));
        
                // Dimensions and Weight
                const dimensions = `${box.width}×${box.height}×${box.longitude} CM`;
                const weight = `${box.weight}kg`;
                rows.push([dimensions, weight].join(','));
        
                // Product name and code
                rows.push([escapeComma(product.productName) || escapeComma(product.name) || '', product.code || ''].join(','));

                const customizedProduct = product.productVariants[0]
        
                // Characteristics: Name, Value Name, Value Code
                if (customizedProduct.characteristics && customizedProduct.characteristics.length > 0) {
                    customizedProduct.characteristics.forEach(char => {
                        rows.push([escapeComma(char.characteristicName) || '', escapeComma(char.valueName) || '', char.code || ''].join(','));
                    });
                }
        
                // Add 2 blank lines between product configurations
                rows.push('', '');
            });
        });
      
        // Convert rows array to CSV string with UTF-8 BOM for proper encoding
        const csvData = '\uFEFF' + rows.join('\n');
        return csvData;
    }
      
    // Example usage to trigger download
    function downloadOrderCSV() {
        const csvData = generateOrderCSV(currentOrder);
        const fileName = "Productos en Orden - " + currentOrder._id.split('-')[0] + ".csv";
        
        // Create temporary link to trigger download
        const link = document.createElement('a');
        link.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csvData));
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
      
    

    const handleChange = (value, indx) => {
        const newBoxes = JSON.parse(JSON.stringify(boxes));
        newBoxes[indx].isReadyForCollection = value;
        setBoxes(newBoxes);

        if(newBoxes.every(item => !!item.isReadyForCollection)){
            setChecked(true);
        }else{
            setChecked(false);
        }
    };

    const checkUpdate = async () => {
        if(isLoading) return;
    
        if(boxes.every((item, indx) => !!item.isReadyForCollection == !!currentOrder.shipment[indx].isReadyForCollection)){
            onCloseModal();
        }else{
            setIsLoading(true);
            const result = await orderUpdate({_id: currentOrder._id, isPackaged: checked, shipment: boxes});
            setIsLoading(false);
            if(result.result){
                reloadTableData();
                enqueueSnackbar(result.message, {
                    variant: 'success',
                    style: { 
                      backgroundColor: '#FC581F',
                      Width: '100%',
                      justifyContent: 'center'
                    }
                });
                onCloseModal();
            }else{
                enqueueSnackbar(result.message,{
                    variant: 'error'
                });
            }
        }
    }

    const handleRetryShipment = async () => {
        if (isLoading || !canRetryShipment) return;

        setIsLoading(true);
        const result = await orderRetryShipment(currentOrder._id);
        setIsLoading(false);

        if (result.order) {
            setCurrentOrder(result.order);
            setChecked(!!result.order.isPackaged);
            setBoxes(JSON.parse(JSON.stringify(result.order.shipment || [])));
        }

        enqueueSnackbar(result.message, {
            variant: result.result ? 'success' : 'error',
            ...(result.result
                ? {
                      style: {
                          backgroundColor: '#FC581F',
                          Width: '100%',
                          justifyContent: 'center',
                      },
                  }
                : {}),
        });

        if (result.result) {
            reloadTableData();
        }
    };

    return (
        <Modal
            disableEscapeKeyDown
            slotProps={{backdrop: {onClick: null}}}
            open={openModal}
            onClose={onCloseModal}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            sx={{
                backdropFilter: 'blur(2px)',
                BackdropProps:{
                onClick: null
                },
                '& .MuiModal-backdrop': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)'
                }
            }}
        >
            <Box sx={styleModal}>
                <Box sx={{display: 'flex', flexDirection: 'column', gap: {xs: '1.5rem', lg: '1.25rem'}, maxHeight: "85vh", width:'100%', overflow: 'auto'}}>
                    <Grid container rowSpacing={'1rem'} columnSpacing={'1rem'}>
                        <Grid item xs={12}>
                            <Box sx={{display: 'flex', justifyContent: "space-between", alignItems: 'center'}}>
                                <Box sx={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                                    <Typography sx={{color:'#303030', fontSize: '1.25rem', fontWeight:'bold', ...customStyle.twoLinesOverflow }}>
                                        {`ID de Orden No. ${currentOrder._id.split('-')[0]}`}
                                    </Typography>
                                </Box>
                                <IconButton sx={{ padding: 0}} onClick={checkUpdate}>
                                    <CancelIcon sx={{color: '#FC581F', fontSize:'1.8rem'}}/>
                                </IconButton>
                            </Box>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{display: { xs:'flex'}, flexDirection: {xs: 'column', md: 'row'}, gap: '0.5rem' }}>
                                <Box sx={{display: 'flex', padding: '0.25rem 1rem', alignItems: 'center', width: 'fit-content', borderRadius: '6.25rem',  border: '1px solid #F4F4F4', background: '#E9E9E9'}}>
                                    <Typography sx={{color: '#303030', fontSize: {xs: '0.875rem', lg: '1rem'}, fontWeight: 500, lineHeight: {xs: '1rem', lg: '1.125rem'}, letterSpacing: '0.01563rem' }}>
                                        {`Número de autorización: ${!!currentOrder.paymentId ? currentOrder.paymentId : 'S/N'}`}
                                    </Typography>
                                </Box>
                                <ChipLabel label={(chipStatus[currentOrder.status] || chipStatus.pending).label} background={(chipStatus[currentOrder.status] || chipStatus.pending).background} />
                            </Box>    
                        </Grid>
                        {!!shipmentRetryError && (
                            <Grid item xs={12}>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '0.5rem',
                                        p: '0.875rem 1rem',
                                        borderRadius: '0.5rem',
                                        border: '1px solid #F2B8B5',
                                        background: '#FFF0F0',
                                    }}
                                >
                                    <Typography sx={{ ...customStyle.subtitle, fontWeight: 700, color: '#A3271A' }}>
                                        Último error al crear la guía
                                    </Typography>
                                    <Typography sx={customStyle.description}>
                                        {shipmentRetryError.message}
                                    </Typography>
                                    {!!shipmentRetryError.skydropxMessage && (
                                        <Typography sx={customStyle.description}>
                                            {`Skydropx: ${shipmentRetryError.skydropxMessage}`}
                                        </Typography>
                                    )}
                                    <Typography sx={customStyle.description}>
                                        {`Código: ${shipmentRetryError.code || 'shipmentError'}`}
                                    </Typography>
                                    <Typography sx={customStyle.description}>
                                        {`Fecha: ${shipmentRetryError.at ? formatDate(shipmentRetryError.at, 'D MMMM, YYYY HH:mm') : 'N/A'}`}
                                    </Typography>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Typography sx={customStyle.subtitle}>
                                Nombre de usuario: <Typography variant={'span'} sx={customStyle.description}>{!!currentOrder.clientInfo ? currentOrder.clientInfo.name: ''}</Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography sx={customStyle.subtitle}>
                                Dirección: <Typography variant={'span'} sx={customStyle.description}>{!!currentOrder.clientInfo ? `${currentOrder.clientInfo.address1} ${currentOrder.clientInfo.colony}, C.P. ${currentOrder.clientInfo.postalCode}, ${currentOrder.clientInfo.city}, ${currentOrder.clientInfo.region}`: ''}</Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={'auto'}>
                            <Typography sx={customStyle.subtitle}>
                                Fecha: <Typography variant={'span'} sx={customStyle.description}>{formatDate(currentOrder.paidAt, 'D MMMM, YYYY')}</Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={!!currentOrder.invoiceRequested ? 9 : 12}>
                            <Typography sx={customStyle.subtitle}>
                                Precio Dólar: <Typography variant={'span'} sx={customStyle.description}>{!!currentOrder.dollarPrice ? `${currencyFormat(currentOrder.dollarPrice)} MXN` : ''}</Typography>
                            </Typography>
                        </Grid>
                        {   !!currentOrder.invoiceRequested &&
                            (<>
                                <Grid item xs={12} md={'auto'}>
                                    <Typography sx={customStyle.subtitle}>
                                        Forma de pago: <Typography variant={'span'} sx={customStyle.description}>{paymentMethodType[currentOrder.paymentMethod]}</Typography>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} md={true}>
                                    <Typography sx={customStyle.subtitle}>
                                        Usos de CFDI: <Typography variant={'span'} sx={customStyle.description}>{!!currentOrder.cfdiUse ? currentOrder.cfdiUse : 'N/A' }</Typography>
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Box sx={{ display: 'flex', gap: '0.25rem', alignItems: 'center' }}>
                                        <Typography sx={customStyle.subtitle}>Factura solicitada:</Typography>
                                        <Typography onClick={getCfdi} sx={{...customStyle.description, color: !isLoading ? '#2F69FF': '#a1a1a1', textDecorationLine: 'underline', cursor:  !isLoading ? 'pointer': 'default', lineHeight: '1.125rem'}}>
                                            Imprimir CFDI
                                        </Typography>
                                    </Box>
                                </Grid>
                            </>
                            )
                        }
                        <Grid item xs={12}>
                            <Divider sx={{ borderColor:'#E9E9E9'}}/>

                        </Grid>
                    </Grid>
                    <Box id='3/3' sx={{display:'flex', flexDirection: 'column', gap: '1.25rem'}} >
                        <Box sx={{display: 'flex', justifyContent: "space-between", alignItems: 'center', px: '1.25rem'}}>
                            <Typography sx={customStyle.subtitleBold}>Empaquetado:</Typography>
                            <Box sx={{display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end'}}>
                                    <ButtonStyled
                                        text='Reintentar guía'
                                        primary={false}
                                        isDisabled={!canRetryShipment || isLoading}
                                        customStyle={{ width: 'fit-content', py: '0.5rem', px: '0.75rem' }}
                                        handleClick={handleRetryShipment}
                                    />
                                    <Typography sx={{fontSize: {xs:'0.875rem', lg: '1rem'}, fontWeight: 700, lineHeight: {xs: '1rem', lg: '1.125rem'}, fontWeight: '500', color: '#303030'}}>Orden empaquetada:</Typography>
                                    <IOSSwitch 
                                        sx={{ m: 0}} 
                                        checked={checked}
                                        inputProps={{ 'aria-label': 'controlled' }}  
                                    />
                                </Box>
                        </Box>
                        <Box sx={{display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                            <Box sx={{display:'flex', padding: '1.25rem 1rem', gap: '1.5rem', flexDirection:'column', borderRadius: '0.625rem', border: '1px solid #E9E9E9', overflow: 'auto', maxHeight: {xs:'20vh', lg: '22vh', xl: '25vh'} }}>
                                {boxes.map((box, indx) => (
                                    <React.Fragment key={indx}>
                                        <OrderBoxDetail box={box} indx={indx} isLoading={isLoading} handleCheckedChange={handleChange} orderStatus={currentOrder.status}/>
                                        {indx+1 < boxes.length && <Divider sx={{ borderColor:'#BEBDBD'}}/> }
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Box>
                        <Divider sx={{ borderColor:'#E9E9E9', mx: '1.25rem'}}/>
                        <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', flexWrap: 'nowrap', gap: '1rem', width: '100%'}}>
                            <ButtonStyled 
                                text='Descargar Productos a Empaquetar' 
                                customStyle={{width: 'fit-content'}}
                                primary={true} 
                                handleClick={() => {
                                    // console.log("BOXES", boxes); 
                                    downloadOrderCSV();
                                }} 
                            />
                            <Box sx={{display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap:'0.75rem', width: '100%'}} >
                                <Box sx={customStyle.priceResumeDiv}>
                                    <Typography sx={customStyle.subtitleBold}>Subtotal</Typography>
                                    <Typography sx={customStyle.description}>{!!currentOrder.mercadopagoFee ? `${currencyFormat(currentOrder.totalPaid)} MXN` : ''}</Typography>
                                </Box>
                                <Box sx={customStyle.priceResumeDiv}>
                                    <Typography sx={customStyle.subtitleBold}>Cargos por venta</Typography>
                                    <Typography sx={customStyle.description}>{!!currentOrder.mercadopagoFee ? `- ${currencyFormat(currentOrder.mercadopagoFee)} MXN` : ''}</Typography>
                                </Box>
                                <Box sx={customStyle.priceResumeDiv}>
                                    <Typography sx={customStyle.subtitleBold}>Envíos</Typography>
                                    <Typography sx={customStyle.description}>{!!currentOrder.shippingFee ? ` - ${currencyFormat(currentOrder.shippingFee)} MXN` : ''}</Typography>
                                </Box>
                                <Divider sx={{ borderColor:'#FC581F'}}/>
                                <Box sx={customStyle.priceResumeDiv}>
                                    <Typography sx={{fontSize: {lg:'1.125rem'}, lineHeight: {xs: '1.125rem', lg: '1.25rem'}, fontWeight: 'bold'}}>Monto Total:</Typography>
                                    <Typography sx={{fontSize: {lg:'1.125rem'}, lineHeight: {xs: '1.125rem', lg: '1.25rem'}, fontWeight: 'bold'}}>
                                        {`${currencyFormat(Number(currentOrder.totalPaid) - Number(currentOrder.shippingFee) - Number(currentOrder.mercadopagoFee))} MXN`}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Modal>
    )
}

export default OrderDetailModal